/**
 * PaymentPage styles — CWR 2.6.
 *
 * Ports the payment-page mockup onto the INJECTED per-tenant client tokens (never
 * the mockup's literal :root hex). Structure mirrors the 2.5 form page / 2.4 detail
 * page: a fixed page, an inner scroller, a glass Back, a pinned BookingHero, a
 * rising sheet, and — unlike 2.5's inline CTA — a floating, keyboard-aware pay bar
 * (the only keyboard interaction is the Stripe iframe, so the bar rides above the
 * keyboard via the viewport-delta bottom offset). The dark scrim + neutral grab
 * handle are image-space (sanctioned literals, same as 2.4/2.5).
 */

/* ── Page + scroll (fullscreen adaptation of the mockup .iphone-frame / .app) ── */
.page {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--app-viewport-stable-height, 100dvh);
  max-width: 480px;
  margin: 0 auto;
  background: var(--bg-page);
  color: var(--text-primary);
  overflow: hidden;
  font-family: var(--font-body);
}
.scroll {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: var(--app-viewport-height, 100dvh);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  z-index: 2;
  /* Clear the floating pay bar (~80px) + safe-area so the secure note isn't hidden. */
  padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 100px);
  overscroll-behavior: none;
  overflow-anchor: none;
}
.scroll::-webkit-scrollbar {
  display: none;
}
/* Transparent window in the scroller revealing the pinned hero underneath (= hero height). */
.heroSpacer {
  height: 330px;
  flex-shrink: 0;
  pointer-events: none;
}

/* ── Back button — glass icon square, identical to the 2.5 form / 2.4 detail page ── */
/* rd-bookings — positioning only; the glass square + arrow are owned by the
   shared FlowBackButton (design D6). */
.back {
  position: fixed;
  top: calc(
    var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px)) + var(--tg-content-safe-area-inset-top, 0px) + 12px
  );
  left: 18px;
  z-index: 80;
}

/* ── Rising sheet — scrolls UP over the pinned hero (matches the 2.4/2.5 pages) ── */
.sheet {
  position: relative;
  z-index: 1;
  border-radius: 48px 48px 0 0;
  background: var(--bg-page);
  /* Upward only: the negative spread keeps the blur from spilling past the flat
     bottom edge, which drew a dark band under a short sheet on non-black looks. */
  box-shadow: 0 -10px 16px -6px rgba(0, 0, 0, 0.24);
  min-height: 60vh;
}
.grab {
  width: 38px;
  height: 5px;
  border-radius: 3px;
  background: rgba(var(--fg-page-rgb), 0.22);
  margin: -4px auto 0;
}
.body {
  padding: 16px 20px 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Title + total ── */
.ptitle {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 21px;
  color: var(--text-primary);
}
.totalRow {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 2px;
}
.totalLabel {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}
.totalValue {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 28px;
  color: var(--accent);
  letter-spacing: -0.01em;
}

/* ── Payment method section ── */
.sectionLabel {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  margin-bottom: 10px;
}
.paySec {
  display: flex;
  flex-direction: column;
  gap: 11px;
}

/* "or pay another way" divider between the card surface and the Venmo source row */
.or {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-tertiary);
  font-size: 11px;
}
.or::before,
.or::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--border-subtle);
}

/* "Pay with card instead" toggle (when Venmo is the active source) — mirrors the
   mockup's `.saved-alt` affordance. */
.altLink {
  align-self: center;
  background: none;
  border: 0;
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 600;
  color: var(--accent-hot);
  cursor: pointer;
  padding: 2px;
}

/* ── Banners (decline / expired / degrade / hard-block / settling) ── */
.banner {
  display: flex;
  gap: 11px;
  align-items: flex-start;
  padding: 12px 13px;
  border-radius: 14px;
  background: rgba(var(--accent-rgb), 0.1);
  border: 1px solid rgba(var(--accent-rgb), 0.3);
}
.banner svg {
  width: 18px;
  height: 18px;
  color: var(--accent-hot);
  flex-shrink: 0;
  margin-top: 1px;
  stroke-width: 2;
}
.bannerTitle {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 13px;
}
.bannerBody {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
  line-height: 1.45;
}

/* ── Reserved-slot pill (D2 — kept on /payment for parity with the form page) ── */
.reservedInfo {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  padding: 7px 11px;
  background: rgba(var(--accent-rgb), 0.1);
  border-radius: 10px;
  font-size: 12.5px;
  color: var(--accent);
}
.reservedInfo svg {
  width: 14px;
  height: 14px;
}

/* ── Secure note ── */
.secure {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 11px;
  color: var(--text-tertiary);
}
.secure svg {
  width: 12px;
  height: 12px;
  stroke-width: 2;
}

/* ── Floating, keyboard-aware pay bar (absolute overlay — sanctioned for keyboard).
   bottom rises by the keyboard height (stable − current viewport) so it sits above
   the keyboard while a Stripe card field is focused. The viewport-height var lands in
   one step (ahead of the keyboard's own slide), so without a transition the bar would
   TELEPORT up and the keyboard would then animate under it — a detached "pop". A tuned
   transition on `bottom` lets the bar RIDE to position (both open + collapse), matching
   the ~250ms iOS keyboard motion. (Transitioning `bottom` rather than `transform`
   sidesteps the WKWebView transform-at-rest compositing bug and keeps the exact
   resting/raised positions.) ── */
.formbar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(
    var(--app-viewport-stable-height, 0px) - var(--app-viewport-height, var(--app-viewport-stable-height, 0px))
  );
  transition: bottom 250ms cubic-bezier(0.32, 0.72, 0, 1);
  z-index: 70;
  padding: 11px 16px calc(env(safe-area-inset-bottom, 0px) + 14px);
  background: linear-gradient(180deg, rgba(var(--bg-page-rgb), 0) 0%, rgba(var(--bg-page-rgb), 0.88) 32%, var(--bg-page) 100%);
}
.payBtn {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  border-radius: 14px;
  background: var(--accent-orb);
  color: var(--text-on-accent);
  font-family: var(--font-body);
  font-size: 15.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
  box-shadow: var(--glow-amber, 0 0 32px rgba(var(--accent-rgb), 0.28));
}
.payBtn svg {
  width: 16px;
  height: 16px;
  stroke-width: 2.4;
}
.payBtn:disabled,
.payBtnDisabled {
  background: var(--bg-inset);
  color: var(--text-muted);
  box-shadow: none;
  border: 1px solid var(--border-soft);
}

/* ── Loading + error states (themed; no ProgressBar) ── */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  gap: 16px;
  color: var(--text-secondary);
}
.loading p {
  margin: 0;
}
.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--glass-bg-strong);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 28px;
  text-align: center;
}
.error h2 {
  font-family: var(--font-display);
  font-size: 24px;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}
.error p {
  color: var(--text-secondary);
  margin: 0 0 24px 0;
  max-width: 280px;
}
.error button {
  padding: 12px 24px;
  background: var(--accent);
  color: var(--text-on-accent);
  border: none;
  border-radius: 14px;
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}
